import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '@/api/client';
import { cave, type CaveMapDTO } from '@/api/endpoints';
import { Icon } from '@/components/ui/Icon';
import { ErrorNote, Loading, Panel, ScreenBody, ScreenHeader } from '@/components/ui/primitives';
import { useRun } from '@/store/run';
import { useSession } from '@/store/session';

/**
 * Where to dig — and, for everywhere else, how to get there.
 *
 * The screen is a list of every cave in the game, **including the ones the player
 * cannot enter**. That is the whole design. A map screen that only shows what you
 * already have is a dropdown; showing "Glass Reef — 3 of 6 layers, found below 220 m
 * in the Abandoned Mine" is simultaneously the reward, the goal and the instructions,
 * and it is the only place the game explains why depth is worth anything.
 *
 * ## Choosing is not entering
 *
 * Tapping an open cave writes a preference and goes back. It does **not** open a run:
 * `Corridor` does that, once, when the player taps DIG, and doing it here would mean
 * two places in the app can start a descent and burn a torch.
 *
 * ## The lock is drawn, never enforced
 *
 * `unlocked` and `level_ok` decide what this screen greys out, and that is all they
 * do. `POST /cave/enter` re-checks the player's `cave_maps` rows on every descent and
 * answers `CAVE_LOCKED`, so a client that sends a key it was never offered is refused
 * by the server rather than by this component (money rule 5).
 */
export function Maps({ onBack, onDig }: { onBack: () => void; onDig?: () => void }) {
  const [rows, setRows] = useState<CaveMapDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const chosen = useRun((s) => s.caveKey);
  const chooseCave = useRun((s) => s.chooseCave);
  const inCave = useRun((s) => s.phase !== 'idle');
  const level = useSession((s) => s.user?.level ?? 1);

  const load = useCallback(() => {
    cave.maps()
      .then((r) => { setRows(r.caves); setError(null); })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load your maps.'));
  }, []);

  useEffect(load, [load]);

  /* A run can finish a map while this screen is mounted underneath — the search
     happens in the cave, not here. The store fires an event rather than calling back
     into this component, because the cave has no idea whether the map screen exists. */
  useEffect(() => {
    window.addEventListener('shwetap:cave-opened', load);
    return () => window.removeEventListener('shwetap:cave-opened', load);
  }, [load]);

  if (!rows) return <Loading />;

  const pick = (row: CaveMapDTO) => {
    if (!row.unlocked || !row.level_ok) return;
    chooseCave(row.key, row.name);
    /* Straight into it if the player is at the surface. Choosing a cave and then
       being returned to a menu to press DIG is one tap of ceremony for a decision
       already made. Mid-run there is nothing to start — the choice applies next
       time, and the banner above says so. */
    if (!inCave && onDig) onDig();
    else onBack();
  };

  return (
    <ScreenBody>
      <ScreenHeader icon={<Icon name="map" size={24} />} title="Maps" onBack={onBack} />
      {error && <ErrorNote>{error}</ErrorNote>}

      {inCave && (
        <p className="mb-3 rounded-md border border-[var(--hairline)] bg-black/30 px-3 py-2
                      text-[11px] text-ink-dim">
          You are underground. Choosing a cave here changes where your <b>next</b> descent
          starts — this run carries on where it is.
        </p>
      )}

      <div className="flex flex-col gap-2.5">
        {rows.map((row) => (
          <CaveCard
            key={row.key}
            row={row}
            level={level}
            current={row.key === chosen}
            onPick={() => pick(row)}
          />
        ))}
      </div>

      <Panel title="HOW MAPS ARE FOUND" className="mt-3">
        <p className="text-[11px] leading-relaxed text-ink-dim">
          Search the walls as you dig. Most of the time there is nothing behind them — but
          deep enough down, in the right cave, one search in about eleven turns up a torn
          layer of somebody else&rsquo;s survey map. Collect every layer and that cave opens.
        </p>
        <p className="mt-2 text-[11px] leading-relaxed text-ink-dim">
          Layers cannot be bought, sold or traded. The only way to a new cave is to go
          and find it.
        </p>
      </Panel>
    </ScreenBody>
  );
}

function CaveCard({ row, level, current, onPick }: {
  row: CaveMapDTO; level: number; current: boolean; onPick: () => void;
}) {
  const open = row.unlocked && row.level_ok;
  /* Two locks, reported separately, because "you need level 8" and "you are missing
     three map layers" are different problems with different next actions. A single
     grey card saying neither is how a finished map reads as a broken button. */
  const needsLevel = row.unlocked && !row.level_ok;
  const pct = row.fragments_required > 0
    ? Math.min(100, (row.fragments / row.fragments_required) * 100)
    : 100;

  return (
    <button
      type="button"
      onClick={onPick}
      disabled={!open}
      aria-current={current ? 'true' : undefined}
      className={`relative w-full overflow-hidden rounded-md border p-3 text-left transition-transform
                  active:translate-y-0.5 disabled:active:translate-y-0
                  ${current ? 'border-[var(--hairline-hi)]' : 'border-[var(--hairline)]'}`}
      style={{
        /* The cave's own colour, at an alpha low enough to stay a hint rather than a
           block of paint. Locked caves keep it: the point of showing a cave you
           cannot enter is that it looks like somewhere worth going. */
        background: `linear-gradient(160deg, ${row.tint}D9, ${row.fog}F2)`,
      }}
    >
      <div className="flex items-start gap-3">
        <span
          className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border
                     border-[var(--hairline)] bg-black/40"
          style={{ color: open ? row.glow : 'var(--text-dim)' }}
          aria-hidden
        >
          <Icon name={open ? 'cave' : 'lock'} size={22} />
        </span>

        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <b className="text-[14px]" style={{ color: open ? '#FFF3DC' : 'var(--text-dim)' }}>
              {row.name}
            </b>
            {current && (
              <span className="rounded bg-black/45 px-1.5 py-0.5 font-pixel text-[6px]
                               tracking-wide text-torch-core">
                CHOSEN
              </span>
            )}
          </span>
          <span className="mt-0.5 block text-[11px] text-ink-dim">
            {row.subtitle || row.description}
          </span>

          <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]">
            <span className="flex items-center gap-1 text-coin tabular">
              <Icon name="coin" size={11} />
              <span className="sr-only">Shwe Coins</span>
              ×{Number(row.coin_multiplier).toFixed(2)}
            </span>
            {row.is_endless && <span className="text-ink-dim">endless</span>}
            <span className="text-ink-dim">{row.zones.length} zones</span>
          </span>
        </span>
      </div>

      {/* What is standing between the player and this cave, if anything. */}
      {!row.unlocked && row.fragments_required > 0 && (
        <div className="mt-2.5 rounded-lg bg-black/45 px-2.5 py-2">
          <div className="flex items-center justify-between text-[10px]">
            <span className="flex items-center gap-1.5 text-ink-dim">
              <Icon name="mapPiece" size={12} />
              <span>MAP LAYERS</span>
            </span>
            <b className="tabular text-ink-hi">{row.fragments} / {row.fragments_required}</b>
          </div>
          <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-white/10">
            <span
              className="block h-full rounded-full transition-[width] duration-500"
              style={{ width: `${pct}%`, background: `linear-gradient(90deg,${row.glow},#FFF3DC)` }}
            />
          </span>
          {row.found_in_name && (
            <p className="mt-1.5 text-[10px] leading-snug text-ink-dim">
              Search the walls of <b className="text-ink-hi">{row.found_in_name}</b> below{' '}
              <b className="text-ink-hi tabular">{row.found_from_depth_m}m</b>.
            </p>
          )}
        </div>
      )}

      {needsLevel && (
        <p className="mt-2.5 rounded-lg bg-black/45 px-2.5 py-2 text-[10px] text-ink-dim">
          Map complete. Opens at <b className="text-ink-hi tabular">level {row.unlock_min_level}</b> —
          you are level <b className="text-ink-hi tabular">{level}</b>.
        </p>
      )}
    </button>
  );
}
